import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Header,
    Button,
    Message,
    Checkbox,
    Icon,
    Label,
    Table,
} from 'semantic-ui-react';
import { LoadingWrapper } from '../common';
import { useAuth } from '../auth';
import { useFormChecklistDetail } from './hooks';
import './FormChecklist.css';

export const FormChecklistDetail = () => {
    const navigate = useNavigate();
    const { userIsIccSupportTeam, isReadOnly } = useAuth();

    const {
        id,
        checklist,
        loaded,
        error,
        clearError,
        checked,
        checkedNoGo,
        saving,
        saveError,
        setSaveError,
        handleCheck,
        handleClearForm,
        handleExport,
        handleDelete,
    } = useFormChecklistDetail();

    const [collapsedSteps, setCollapsedSteps] = useState(new Set());

    const toggleStep = (stepId) => {
        setCollapsedSteps(prev => {
            const next = new Set(prev);
            if (next.has(stepId)) next.delete(stepId);
            else next.add(stepId);
            return next;
        });
    };

    if (!loaded) return <LoadingWrapper loading />;
    if (!checklist) return <Message negative>{error || 'Checklist not found.'}</Message>;

    const sortedSteps = [...(checklist.steps || [])].sort((a, b) => a.order - b.order);

    return (
        <div className="fc-detail-container">
            <Header as="h2" textAlign="center" className="fc-detail-title">
                {checklist.name}
            </Header>

            {error && <Message negative content={error} onDismiss={clearError} />}
            {saveError && <Message negative content={saveError} onDismiss={() => setSaveError(null)} />}

            {(checklist.activity || checklist.condition || checklist.standard) && (
                <Table definition compact className="fc-info-table">
                    <Table.Body>
                        {checklist.activity && (
                            <Table.Row>
                                <Table.Cell>Activity</Table.Cell>
                                <Table.Cell>{checklist.activity}</Table.Cell>
                            </Table.Row>
                        )}
                        {checklist.condition && (
                            <Table.Row >
                                <Table.Cell>Condition</Table.Cell>
                                <Table.Cell>{checklist.condition}</Table.Cell>
                            </Table.Row>
                        )}
                        {checklist.standard && (
                            <Table.Row>
                                <Table.Cell>Standard</Table.Cell>
                                <Table.Cell>{checklist.standard}</Table.Cell>
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>
            )}

            {sortedSteps.length === 0 ? (
                <Message info content="This checklist has no steps yet." />
            ) : (
                <div className="fc-detail-table-wrapper">
                <Table celled structured unstackable>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell colSpan={4} className="fc-table-main-header">
                                Steps, Tasks, and Performance Measures
                            </Table.HeaderCell>
                        </Table.Row>
                        <Table.Row>
                            <Table.HeaderCell collapsing></Table.HeaderCell>
                            <Table.HeaderCell>Step / Task</Table.HeaderCell>
                            <Table.HeaderCell collapsing textAlign="center">Go</Table.HeaderCell>
                            <Table.HeaderCell collapsing textAlign="center">No Go</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {sortedSteps.map(step => {
                            const sortedTasks = [...(step.tasks || [])].sort((a, b) => a.order - b.order);
                            const goCount = sortedTasks.filter(t => checked[t.id]).length;
                            const noGoCount = sortedTasks.filter(t => checkedNoGo[t.id]).length;
                            const total = sortedTasks.length;
                            const isExpanded = !collapsedSteps.has(step.id);
                            return (
                                <React.Fragment key={step.id}>
                                    <Table.Row
                                        className="fc-step-row fc-step-row-clickable"
                                        onClick={() => toggleStep(step.id)}
                                    >
                                        <Table.Cell collapsing>
                                            <Icon name={isExpanded ? 'chevron down' : 'chevron right'} />
                                            <strong>{step.order}</strong>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <strong>{step.name}</strong>
                                        </Table.Cell>
                                        <Table.Cell textAlign="center" width={2}>
                                            <Label
                                                size="small"
                                                color={goCount === total && total > 0 ? 'green' : undefined}
                                            >
                                                {goCount} / {total}
                                            </Label>
                                        </Table.Cell>
                                        <Table.Cell textAlign="center" width={2}>
                                            <Label
                                                size="small"
                                                color={noGoCount > 0 ? 'red' : undefined}
                                            >
                                                {noGoCount} / {total}
                                            </Label>
                                        </Table.Cell>
                                    </Table.Row>
                                    {isExpanded && sortedTasks.map((task, taskIdx) => (
                                        <Table.Row key={task.id}>
                                            <Table.Cell></Table.Cell>
                                            <Table.Cell>{task.description}</Table.Cell>
                                            <Table.Cell textAlign="center">
                                                <Checkbox
                                                    checked={!!checked[task.id]}
                                                    onChange={() => handleCheck(task.id, 'go')}
                                                />
                                            </Table.Cell>
                                            <Table.Cell textAlign="center">
                                                <Checkbox
                                                    checked={!!checkedNoGo[task.id]}
                                                    onChange={() => handleCheck(task.id, 'nogo')}
                                                />
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                </React.Fragment>
                            );
                        })}
                    </Table.Body>
                </Table>

            </div>
            )}

            <div className="fc-actions-bar">
                <Button onClick={handleClearForm}>
                    <Icon name="redo" />
                    Clear Form
                </Button>
                <Button onClick={handleExport}>
                    <Icon name="print" />
                    Export
                </Button>
                {userIsIccSupportTeam && !isReadOnly && (
                    <Button primary onClick={() => navigate(`/documents/form-checklists/${id}/edit`)}>
                        <Icon name="edit" />
                        Edit
                    </Button>
                )}
                {userIsIccSupportTeam && !isReadOnly && (
                    <Button color="red" onClick={handleDelete}>
                        <Icon name="trash" />
                        Delete
                    </Button>
                )}
            </div>
        </div>
    );
};
